import React, { FC, useEffect, useMemo, useState } from "react";
import ErrorBoundaryForSection from "../errorBoundaryForSection";
import { AdminFormFieldDropdown, TSelectboxOption } from "./formFields";

type AdminFormFieldDurationInputProps = {
  label: string;
  id: string;
  name: string;
  max?: number;
  value?: string | number | null;
  showSeconds?: boolean;
  hideHours?: boolean;
  minutesAsInput?: boolean;
  maxMinutes?: number;
  touched?: boolean;
  error?: string;
  disabled?: boolean;
  detailsFormField?: boolean;
  customWrapperClass?: string;
  onChange?: (totalSeconds: number) => void;
  onBlur?: () => void;
  valuePreviewFilter?: (value: string) => string;
  loading?: boolean;
};

const toSafeInt = (value: unknown) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const splitSeconds = (value: unknown) => {
  const total = toSafeInt(value);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { hours, minutes, seconds };
};

const formatDuration = (
  hours: number,
  minutes: number,
  seconds: number,
  showSeconds: boolean
) => {
  const hourValue = `${hours}`.padStart(2, "0");
  const minuteValue = `${minutes}`.padStart(2, "0");
  if (!showSeconds) {
    return `${hourValue}:${minuteValue}`;
  }
  const secondValue = `${seconds}`.padStart(2, "0");
  return `${hourValue}:${minuteValue}:${secondValue}`;
};

export const AdminFormFieldDurationInput: FC<
  AdminFormFieldDurationInputProps
> = ({
  label,
  id,
  name,
  max = 1080, // 45 days in minutes, which is the max limit for BE database field
  value,
  showSeconds = false,
  minutesAsInput = false,
  maxMinutes = 59,
  hideHours = false,
  touched,
  error,
  disabled = false,
  detailsFormField = false,
  customWrapperClass = "col-span-6",
  onChange,
  onBlur,
  valuePreviewFilter = (value) => value,
  loading = false,
}) => {
  const initial = useMemo(() => {
    const splitValue = splitSeconds(value);
    const totalMinutes = Math.floor(toSafeInt(value) / 60);
    return {
      hours: minutesAsInput ? 0 : splitValue.hours,
      minutes: hideHours
        ? clamp(totalMinutes, 0, maxMinutes)
        : minutesAsInput
          ? clamp(splitValue.minutes, 0, maxMinutes)
          : splitValue.minutes,
      seconds: showSeconds
        ? clamp(splitValue.seconds, 0, 59)
        : splitValue.seconds,
    };
  }, [value, hideHours, minutesAsInput, maxMinutes, showSeconds]);
  const [hours, setHours] = useState<number>(initial.hours);
  const [minutes, setMinutes] = useState<number>(initial.minutes);
  const [seconds, setSeconds] = useState<number>(initial.seconds);

  useEffect(() => {
    setHours(initial.hours);
    setMinutes(initial.minutes);
    setSeconds(initial.seconds);
  }, [initial]);

  const fieldHasError = detailsFormField
    ? !disabled && !!error
    : touched && !!error;
  const labelClass = fieldHasError
    ? "text-field-error-dark"
    : "text-field-label-valid";
  const inputClass = fieldHasError
    ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
    : "border-gray-200 text-field-label-valid";

  const updateValue = (
    nextHours: number,
    nextMinutes: number,
    nextSeconds: number
  ) => {
    const totalSeconds =
      (hideHours ? 0 : nextHours) * 3600 +
      nextMinutes * 60 +
      (showSeconds ? nextSeconds : 0);
    onChange?.(totalSeconds);
  };

  const durationPreview =
    hideHours && showSeconds
      ? `${`${minutes}`.padStart(2, "0")}:${`${seconds}`.padStart(2, "0")}`
      : hideHours
        ? `${minutes}`
        : formatDuration(hours, minutes, seconds, showSeconds);

  return (
    <div className={customWrapperClass}>
      <ErrorBoundaryForSection wrapClass="w-full h-full" fontSize="compact">
        <label
          htmlFor={hideHours ? `${id}-minutes` : id}
          className={`flex justify-between text-sm font-display font-semibold ${labelClass}`}
        >
          {label}
          <span>{valuePreviewFilter(durationPreview)}</span>
        </label>

        <div className="mt-1 grid grid-cols-12 gap-2">
          {!hideHours && (
            <div className={showSeconds ? "col-span-4" : "col-span-6"}>
              <input
                id={id}
                name={name}
                type="number"
                min={0}
                max={max}
                step={1}
                value={hours}
                disabled={disabled}
                onBlur={onBlur}
                onChange={(event) => {
                  const nextHours = toSafeInt(event.target.value);
                  setHours(nextHours);
                  updateValue(nextHours, minutes, seconds);
                }}
                className={`w-full h-11 rounded-md text-sm px-3 enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none ${disabled ? "bg-gray-100" : "bg-white"} border ${inputClass}`}
              />
              <p className="mt-1 text-[10px] text-gray-400">Hours</p>
            </div>
          )}

          <div
            className={
              hideHours
                ? showSeconds
                  ? "col-span-6"
                  : "col-span-12"
                : showSeconds
                  ? "col-span-4"
                  : "col-span-6"
            }
          >
            {minutesAsInput || hideHours ? (
              <input
                id={`${id}-minutes`}
                name={`${name}-minutes`}
                aria-label="Minutes"
                type="number"
                min={0}
                max={maxMinutes}
                step={1}
                value={minutes}
                disabled={disabled}
                onBlur={onBlur}
                onChange={(event) => {
                  const nextMinutes = clamp(
                    toSafeInt(event.target.value),
                    0,
                    maxMinutes
                  );
                  setMinutes(nextMinutes);
                  updateValue(hours, nextMinutes, seconds);
                }}
                className={`w-full h-11 rounded-md text-sm px-3 enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none ${disabled ? "bg-gray-100" : "bg-white"} border ${inputClass}`}
              />
            ) : (
              <AdminFormFieldDropdown
                label={false}
                id={`${id}-minutes`}
                name={`${name}-minutes`}
                value={Number(minutes || 0)
                  .toString()
                  .padStart(2, "0")}
                loadingData={loading}
                disabled={disabled}
                onBlur={onBlur}
                onChange={(event) => {
                  const nextMinutes = toSafeInt(event?.value || 0);
                  setMinutes(nextMinutes);
                  updateValue(hours, nextMinutes, seconds);
                }}
                options={
                  [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
                    (minute) => ({
                      label: `${minute}`.padStart(2, "0"),
                      value: (minute || 0).toString().padStart(2, "0"),
                    })
                  ) satisfies TSelectboxOption[]
                }
                dimensionClass="w-full h-11"
                customSelectboxClass=""
                customWrapperClass="col-span-6 -mt-1"
              />
            )}
            <p className="mt-1 text-[10px] text-gray-400">Minutes</p>
          </div>

          {showSeconds && (
            <div className={hideHours ? "col-span-6" : "col-span-4"}>
              <input
                id={`${id}-seconds`}
                name={`${name}-seconds`}
                aria-label="Seconds"
                type="number"
                min={0}
                max={59}
                step={1}
                value={seconds}
                disabled={disabled}
                onBlur={onBlur}
                onChange={(event) => {
                  const nextSeconds = clamp(
                    toSafeInt(event.target.value),
                    0,
                    59
                  );
                  setSeconds(nextSeconds);
                  updateValue(hours, minutes, nextSeconds);
                }}
                className={`w-full h-11 rounded-md text-sm px-3 enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none ${disabled ? "bg-gray-100" : "bg-white"} border ${inputClass}`}
              />
              <p className="mt-1 text-[10px] text-gray-400">Seconds</p>
            </div>
          )}
        </div>

        {fieldHasError && (
          <p className="bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1">
            {error}
          </p>
        )}
      </ErrorBoundaryForSection>
    </div>
  );
};

export const valuePreviewFilterSessionTimeout = (value: string) => {
  if (value === "00:00") {
    return "Unlimited";
  }

  const [hoursPart, minutesPart] = value.split(":");
  const hours = Number(hoursPart);

  if (
    minutesPart === "00" &&
    Number.isInteger(hours) &&
    hours > 0 &&
    hours % 24 === 0
  ) {
    const days = hours / 24;
    return `${days} ${days === 1 ? "day" : "days"}`;
  }

  return value;
};
